import {
  getAWSResourceArnFromHybridCatalogId,
  getHybridCatalogResourceArnFromHybridCatalogId,
} from 'src/components/utils/arnUtil';
import {
  getAllMetaDataFieldsForForm,
  getAllMetaDataValuesForFormAndResource,
  listAllMetadataFormEntitiesAssociatedForResource,
} from 'src/commons/commonHCApiCalls';
import { ACCOUNT_DETAIL_GLOBAL_FORM_NAME, COST_CENTER_NAME_METADATA_FIELD_KEY } from 'src/commons/constants';
import { removeSpecialCharactersFromValue } from 'src/components/utils/regexUtil';
import { getJobFacetInfoForGivenRunId, getRunsForJobAsset, getVersionsForDatasetAsset } from 'src/api/latticelineage';
import { FACETS_KEY } from 'src/components/lineage/latticeLineageConstants';
import { isLatticeLineageAvailable } from 'src/api/config';

export const getBaseNodeId = async (datasetId: string, setEmptyLineage: any, setLoadingLineage: any) => {
  if (datasetId) {
    // reset
    const hybridCatalogDatasetArn = getHybridCatalogResourceArnFromHybridCatalogId(datasetId);
    const awsDatasetArn = getAWSResourceArnFromHybridCatalogId(datasetId);
    let accountDetailsFormId = undefined;
    let costCenterFieldId = undefined;
    let filteredCostCenterValue = undefined;
    if (hybridCatalogDatasetArn) {
      accountDetailsFormId = await getAccountDetailsFormIdAttachedToHCResource(
        hybridCatalogDatasetArn,
        setEmptyLineage,
        setLoadingLineage,
      );
    }
    if (accountDetailsFormId) {
      costCenterFieldId = await getCostCenterFieldIdForForm(accountDetailsFormId, setEmptyLineage, setLoadingLineage);
    }
    if (costCenterFieldId) {
      filteredCostCenterValue = await getCostCenterValueFromGivenFormFieldAndResource(
        accountDetailsFormId,
        costCenterFieldId,
        hybridCatalogDatasetArn,
        setEmptyLineage,
        setLoadingLineage,
      );
    }
    if (filteredCostCenterValue) {
      return 'dataset' + '!' + filteredCostCenterValue + '!' + awsDatasetArn;
    }
  }
};

const getAccountDetailsFormIdAttachedToHCResource = async (
  hcDatasetArn: string,
  setEmptyLineage: any,
  setLoadingLineage: any,
) => {
  let allFormsAttachedToResource = [];
  try {
    allFormsAttachedToResource = await listAllMetadataFormEntitiesAssociatedForResource(hcDatasetArn);
    // fetch the required form
    const requiredFormAsList = allFormsAttachedToResource.filter(
      (form) => form?.Name === ACCOUNT_DETAIL_GLOBAL_FORM_NAME,
    );
    if (requiredFormAsList.length == 0) {
      setEmptyLineage(true);
      setLoadingLineage(false);
      return undefined;
    } else {
      return requiredFormAsList[0].MetadataFormId;
    }
  } catch (err) {
    console.log(`Failed to fetch metadata forms for resource: ${hcDatasetArn}`);
    setEmptyLineage(true);
    setLoadingLineage(false);
  }
};

const getCostCenterFieldIdForForm = async (formId: string, setEmptyLineage: any, setLoadingLineage: any) => {
  try {
    let allFieldsForForm = await getAllMetaDataFieldsForForm(formId);
    // fetch the required field
    const requiredFieldAsList = allFieldsForForm.filter((field) => field?.Name === COST_CENTER_NAME_METADATA_FIELD_KEY);
    if (requiredFieldAsList.length == 0) {
      setEmptyLineage(true);
      setLoadingLineage(false);
      return undefined;
    } else {
      return requiredFieldAsList[0].MetadataFieldId;
    }
  } catch (err) {
    console.log(`Failed to fetch metadata fields for form: ${formId}`);
    setEmptyLineage(true);
    setLoadingLineage(false);
  }
};

const getCostCenterValueFromGivenFormFieldAndResource = async (
  formId: string,
  fieldId: string,
  hcDatasetArn: string,
  setEmptyLineage: any,
  setLoadingLineage: any,
) => {
  try {
    let allValuesForFormAndResource = await getAllMetaDataValuesForFormAndResource(formId, hcDatasetArn);
    // fetch the required value
    const requiredValueAsList = allValuesForFormAndResource.filter((value) => value?.Id === fieldId);
    if (requiredValueAsList.length === 0) {
      setEmptyLineage(true);
      setLoadingLineage(false);
      return undefined;
    } else {
      return removeSpecialCharactersFromValue(requiredValueAsList[0].Value?.StringValue);
    }
  } catch (err) {
    console.log(
      `Failed to fetch metadata value for form:${formId}, field: ${fieldId}, resource:${hcDatasetArn}. Reason: ${err}`,
    );
    setEmptyLineage(true);
    setLoadingLineage(false);
  }
};

export const getMetadataInfoForDatasetNodeType = async (namespace: string, assetName: string) => {
  let facetInfo = undefined;
  try {
    let getVersionsResponseData = await getVersionsForDatasetAsset(namespace, assetName);
    // add facet information to the selected node
    let latestVersion = getVersionsResponseData?.versions?.length
      ? getVersionsResponseData?.versions?.reduce((latest, current) =>
          new Date(current.createdAt) > new Date(latest.createdAt) ? current : latest,
        )
      : undefined;
    if (latestVersion && latestVersion[FACETS_KEY] && Object.keys(latestVersion[FACETS_KEY]).length > 0) {
      facetInfo = latestVersion[FACETS_KEY];
    }
  } catch (e) {
    console.log(`Failed to add additional metadata to asset:${assetName}`);
  }
  return facetInfo;
};

export const getMetadataInfoForJobNodeType = async (namespace: string, assetName: string) => {
  let facetInfo = undefined;
  try {
    let getJobRunsResponseData = await getRunsForJobAsset(namespace, assetName);
    let latestRun = getJobRunsResponseData?.runs?.length
      ? getJobRunsResponseData?.runs?.reduce((latest, current) =>
          new Date(current.createdAt) > new Date(latest.createdAt) ? current : latest,
        )
      : undefined;
    if (latestRun) {
      let getJobFacetData = await getJobFacetInfoForGivenRunId(latestRun.id);
      if (getJobFacetData && getJobFacetData[FACETS_KEY] && Object.keys(getJobFacetData[FACETS_KEY]).length > 0) {
        facetInfo = getJobFacetData[FACETS_KEY];
      }
    }
  } catch (e) {
    console.log(`Failed to add additional metadata to asset:${assetName}. Reason:${e}`);
  }
  return facetInfo;
};

export const fetchFacetInfoForNodeId = async (nodeId) => {
  let facetInfo = undefined;
  if (nodeId) {
    try {
      let nodeDetail = nodeId.split('!');
      if (nodeDetail[0] === 'dataset') {
        facetInfo = await getMetadataInfoForDatasetNodeType(nodeDetail[1], nodeDetail[2]);
      }

      if (nodeDetail[0] === 'job') {
        facetInfo = await getMetadataInfoForJobNodeType(nodeDetail[1], nodeDetail[2]);
      }
    } catch (err) {
      console.log(`Failed to fetch facetInfo for node:${nodeId}. Reason:${err}`);
    }
  }
  return facetInfo;
};
